import { useMemo } from "react";
import type { Card, CardCivilization } from "@dm-online/shared";
import { CIV_LABELS, CIV_ORDER } from "@/lib/civ";

// 文明分析の列。既存の文明順（光水闇火自然）に、文明を持たない「ゼロ」を加える。
type CivKey = CardCivilization | "zero";
const ANALYSIS_CIVS: CivKey[] = [...CIV_ORDER, "zero"];
const CIV_LABEL: Record<CivKey, string> = { ...CIV_LABELS, zero: "ゼロ" };

// 文明色の CSS 変数。ゼロ（無色）は前景のグレー。
function civColor(civ: CivKey): string {
  return civ === "zero" ? "var(--muted-foreground)" : `var(--civ-${civ})`;
}

interface CivStat {
  total: number;
  multi: number;
  single: number;
}

interface Stats {
  size: number;
  civ: Record<CivKey, CivStat>;
}

function computeStats(cards: Card[]): Stats {
  const civ = {} as Record<CivKey, CivStat>;
  for (const c of ANALYSIS_CIVS) civ[c] = { total: 0, multi: 0, single: 0 };

  for (const card of cards) {
    // 文明: 多色カードは含む全文明に加算する
    const civs = card.civilization;
    if (civs.length === 0) {
      civ.zero.total += 1;
    } else {
      const isMulti = civs.length >= 2;
      for (const c of civs) {
        civ[c].total += 1;
        if (isMulti) civ[c].multi += 1;
        else civ[c].single += 1;
      }
    }
  }

  return { size: cards.length, civ };
}

// 表の共通スタイル
const TH = "border-b border-border px-1 py-1.5 font-medium text-muted-foreground";
// 列見出し（ヘッダー行）は本文より1段小さい文字
const HEAD = `${TH} text-xs`;
const TD = "border-b border-border px-1 py-1.5";

function Table({ children }: { children: React.ReactNode }) {
  return (
    // 罫線は行の区切り（横線）だけ。最終行の下線は出さない
    <table className="w-full table-fixed border-collapse text-center text-sm [&_tbody_tr:last-child>*]:border-b-0">
      {children}
    </table>
  );
}

interface Props {
  // 集計対象のカード（コピーを含む配列）。通常はメインデッキ40枚。
  cards: Card[];
}

// デッキの文明バランスを示す分析ビュー。
export default function DeckAnalysis({ cards }: Props) {
  const stats = useMemo(() => computeStats(cards), [cards]);

  if (stats.size === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">カードを追加すると分析が表示されます</p>;
  }
  return <CivView stats={stats} />;
}

function CivView({ stats }: { stats: Stats }) {
  // デッキに入っている文明だけを行にする
  const civs = ANALYSIS_CIVS.filter((civ) => stats.civ[civ].total > 0);

  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1 basis-0">
        <CivTable stats={stats} civs={civs} />
      </div>
      <div className="flex min-w-0 flex-1 basis-0 justify-center">
        <CivBars stats={stats} civs={civs} />
      </div>
    </div>
  );
}

// 文明ごとの横棒。多色カードは含む全文明に数えるため、棒は文明ごとに独立した
// 「デッキ枚数に対する、その文明を含むカードの比率」を示す（棒どうしの合計は 100% にならない）。
function CivBars({ stats, civs }: { stats: Stats; civs: CivKey[] }) {
  const share = (n: number) => (n / stats.size) * 100;

  // 左の表と行の高さを揃える: 見出し行の分だけ空けて、以降 1 行 = 1 文明
  return (
    <div className="w-full pt-[28.5px]">
      <ul aria-label="文明ごとの枚数（デッキに対する割合）">
        {civs.map((civ) => {
          const { total } = stats.civ[civ];
          return (
            <li key={civ} className="flex h-[33px] items-center gap-2 text-xs" title={`${CIV_LABEL[civ]} ${total}枚`}>
              <span className="w-7 shrink-0 font-semibold">{CIV_LABEL[civ]}</span>
              <div className="h-3 min-w-0 flex-1 overflow-hidden rounded-sm bg-muted/40">
                <div className="h-full" style={{ width: `${share(total)}%`, backgroundColor: civColor(civ) }} />
              </div>
              <span className="w-8 shrink-0 text-right tabular-nums text-muted-foreground">
                {Math.round(share(total))}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CivTable({ stats, civs }: { stats: Stats; civs: CivKey[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <th className={`w-14 ${HEAD}`} />
          <th className={`${HEAD} font-semibold`}>合計</th>
          <th className={`${HEAD} font-semibold`}>多色</th>
          <th className={`${HEAD} font-semibold`}>単色</th>
        </tr>
      </thead>
      <tbody className="tabular-nums">
        {civs.map((civ) => (
          <tr key={civ}>
            <th className="border-b border-border px-1 py-1.5 font-semibold">{CIV_LABEL[civ]}</th>
            <td className={`${TD} font-semibold`}>{stats.civ[civ].total}</td>
            {civ === "zero" ? (
              <>
                <NaCell />
                <NaCell />
              </>
            ) : (
              <>
                <td className={TD}>{stats.civ[civ].multi}</td>
                <td className={TD}>{stats.civ[civ].single}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

// ゼロ文明の多色/単色は該当しないため、ダッシュで無効を表すセル。
function NaCell() {
  return <td className={`${TD} text-muted-foreground/50`}>—</td>;
}
